import {
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
  type UIEvent,
} from "react";
import { ChevronRight, Sparkles, Bookmark, Terminal } from "lucide-react";
import { renderPart as _renderPart } from "@/components/parts";
import ThreadNav from "@/components/ThreadNav";
import { PulseLoader } from "@/components/PulseLoader";
import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";
import { classifyMetaMessage, type MetaMessage } from "@/lib/parts";
import { getSelectedSessionKey, useStore, store } from "@/lib/store";
import type { Message, Part, ResultPart, ThinkingPart } from "@/lib/types";

interface PartRenderContext {
  messageId: string;
  sessionId: string;
}

function FallbackPart({ part }: { part: Part }) {
  if (part.type === "text") {
    return (
      <div className="whitespace-pre-wrap text-sm leading-relaxed text-foreground">
        {part.text}
      </div>
    );
  }
  if (part.type === "tool_use") {
    return (
      <div className="text-xs font-mono rounded-md bg-secondary/30 border border-border/50 p-2">
        <div className="text-primary">{part.tool}</div>
        <pre className="text-muted-foreground overflow-x-auto">
          {JSON.stringify(part.input, null, 2)}
        </pre>
        {part.resultText && (
          <pre className="mt-2 text-foreground/80 overflow-x-auto">
            {part.resultText}
          </pre>
        )}
      </div>
    );
  }
  if (part.type === "tool_result") {
    return (
      <div className="text-xs font-mono rounded-md bg-secondary/20 border border-border/30 p-2 text-muted-foreground">
        <pre className="overflow-x-auto">
          {typeof part.content === "string"
            ? part.content
            : JSON.stringify(part.content, null, 2)}
        </pre>
      </div>
    );
  }
  if (part.type === "system") {
    return (
      <div className="text-[11px] font-mono text-muted-foreground/70 italic">
        [{part.subtype}]
      </div>
    );
  }
  return null;
}

function ThinkingBlock({ part }: { part: ThinkingPart }) {
  const [open, setOpen] = useState(false);
  const streaming = !part.time?.end;
  return (
    <div className="rounded-md border border-border/40 bg-secondary/20">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="w-full flex items-center gap-1.5 px-2 py-1.5 text-xs text-muted-foreground hover:text-foreground"
      >
        <ChevronRight
          className={cn("h-3 w-3 transition-transform", open && "rotate-90")}
        />
        {streaming ? (
          <PulseLoader size={12} ariaLabel="Thinking" />
        ) : (
          <Sparkles className="h-3 w-3" />
        )}
        <span className="italic">thinking</span>
      </button>
      {open && (
        <div className="px-3 pb-2 text-xs italic text-muted-foreground whitespace-pre-wrap">
          {part.text}
        </div>
      )}
    </div>
  );
}

function ResultFooter({ part }: { part: ResultPart }) {
  const cost =
    typeof part.costUsd === "number" ? `$${part.costUsd.toFixed(4)}` : null;
  const dur =
    typeof part.durationMs === "number"
      ? `${(part.durationMs / 1000).toFixed(1)}s`
      : null;
  return (
    <div className="flex items-center gap-2 pt-2 border-t border-border/30 mt-2">
      <Badge
        variant={part.subtype === "error" ? "destructive" : "secondary"}
        className="text-[10px] uppercase"
      >
        {part.subtype}
      </Badge>
      {cost && (
        <Badge variant="outline" className="text-[10px]">
          {cost}
        </Badge>
      )}
      {dur && (
        <Badge variant="outline" className="text-[10px]">
          {dur}
        </Badge>
      )}
    </div>
  );
}

function renderWithFallback(part: Part, ctx: PartRenderContext): ReactNode {
  if (part.type === "thinking") return <ThinkingBlock part={part} />;
  if (part.type === "result") return <ResultFooter part={part} />;
  try {
    const out = (_renderPart as unknown as (
      p: Part,
      c: PartRenderContext,
    ) => ReactNode)(part, ctx);
    if (out) return out;
  } catch {
    // fall through to fallback
  }
  return <FallbackPart part={part} />;
}

/** Bookkeeping turns with nothing worth showing (the caveat, empty output). */
function isHiddenMessage(msg: Message): boolean {
  const meta = classifyMetaMessage(msg);
  if (!meta) return false;
  return meta.kind === "caveat" || (meta.kind === "output" && !meta.text);
}

/** Slash-command invocations and local command output, as system chrome. */
function MetaMessageRow({
  meta,
}: {
  meta: Exclude<MetaMessage, { kind: "caveat" }>;
}) {
  if (meta.kind === "command") {
    return (
      <div className="flex justify-center">
        <div className="max-w-[85%] min-w-0 flex items-center gap-1.5 rounded-md border border-border/40 bg-secondary/20 px-2 py-1 text-[11px] text-muted-foreground">
          <Terminal className="h-3 w-3 shrink-0" />
          <span className="font-mono truncate">
            {meta.args ? `${meta.name} ${meta.args}` : meta.name}
          </span>
        </div>
      </div>
    );
  }
  if (!meta.text) return null;
  return (
    <div className="flex justify-center">
      <div
        className={cn(
          "max-w-[85%] min-w-0 rounded-md border border-border/30 bg-secondary/10 px-2 py-1 text-[11px] font-mono whitespace-pre-wrap [overflow-wrap:anywhere]",
          meta.isError ? "text-destructive/80" : "text-muted-foreground/80",
        )}
      >
        {meta.text}
      </div>
    </div>
  );
}

function MessageCard({ msg }: { msg: Message }) {
  const state = useStore();
  const isUser = msg.role === "user";
  const isSystem = msg.role === "system";

  const meta = useMemo(() => classifyMetaMessage(msg), [msg]);

  const sessionKey = getSelectedSessionKey(state.selected);
  const markers = sessionKey ? state.markers[sessionKey] ?? [] : [];
  const marker = markers.find((m) => m.messageId === msg.id);
  const isMarked = !!marker;

  function toggleMarker(e: React.MouseEvent) {
    e.stopPropagation();
    if (isMarked) {
      store.removeMarker(sessionKey!, marker.id);
    } else {
      store.addMarker({ sessionId: sessionKey!, messageId: msg.id });
    }
  }

  if (meta) {
    if (meta.kind === "caveat") return null;
    return <MetaMessageRow meta={meta} />;
  }

  return (
    <div
      className={cn(
        "flex group",
        isUser ? "justify-end" : "justify-start",
        isMarked && "relative",
      )}
    >
      {isMarked && (
        <div className="absolute left-0 top-0 bottom-0 w-1 bg-accent rounded-l-lg" />
      )}
      <div
        className={cn(
          "max-w-[85%] min-w-0 rounded-lg p-3 glass-border relative",
          isUser
            ? "bg-primary/5 border-l-2 border-l-primary"
            : "bg-card/60",
          isSystem && "opacity-70",
        )}
      >
        <div className="flex items-center gap-2 mb-1.5">
          <span
            className={cn(
              "text-[10px] uppercase tracking-wide font-semibold",
              isUser ? "text-primary" : "text-accent",
            )}
          >
            {isUser ? "You" : isSystem ? "System" : "Claude"}
          </span>
          {msg.model && (
            <Badge variant="outline" className="text-[9px] py-0 h-4">
              {msg.model}
            </Badge>
          )}
          {/* Inside the bubble so it never clips at the viewport edge; visible
              on touch, on hover, and whenever it takes focus. */}
          <button
            type="button"
            onClick={toggleMarker}
            className={cn(
              "ml-auto -my-1 h-7 w-7 shrink-0 rounded-full flex items-center justify-center transition-[opacity,color,background-color]",
              "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
              // Pointer-capable widths hide it until hover/focus; touch layouts
              // keep it on screen since there is no hover there.
              isMarked
                ? "opacity-100 bg-accent/20 text-accent hover:bg-accent/30"
                : "opacity-100 sm:opacity-0 sm:group-hover:opacity-100 sm:group-focus-within:opacity-100 text-muted-foreground hover:text-foreground hover:bg-muted",
            )}
            title={isMarked ? "Remove marker" : "Add marker"}
            aria-label={isMarked ? "Remove marker" : "Add marker"}
            aria-pressed={isMarked}
          >
            <Bookmark className={cn("h-4 w-4", isMarked && "fill-current")} />
          </button>
        </div>
        <div className="space-y-2 min-w-0 overflow-hidden [overflow-wrap:anywhere]">
          {msg.parts.map((part) => (
            <div key={part.id} className="min-w-0">
              {renderWithFallback(part, {
                messageId: msg.id,
                sessionId: msg.sessionId,
              })}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function MessageThread() {
  const state = useStore();
  const sessionId = state.selected.sessionId;
  const sessionKey = getSelectedSessionKey(state.selected);
  const messages = useMemo<Message[]>(
    () =>
      (sessionKey ? state.messages[sessionKey] ?? [] : []).filter(
        (m) => !isHiddenMessage(m),
      ),
    [sessionKey, state.messages],
  );

  const scrollRef = useRef<HTMLDivElement | null>(null);
  const messageRefsRef = useRef<Map<string, HTMLElement>>(new Map());
  const stickRef = useRef(true);
  const lastSessionRef = useRef<string | null>(null);

  // Nav should step through real prompts, not the local-command bookkeeping
  // turns that share the user role.
  const userMessageIds = useMemo(
    () =>
      messages
        .filter((m) => m.role === "user" && !classifyMetaMessage(m))
        .map((m) => m.id),
    [messages],
  );

  useEffect(() => {
    if (lastSessionRef.current !== sessionId) {
      stickRef.current = true;
      lastSessionRef.current = sessionId;
    }
    const el = scrollRef.current;
    if (!el) return;
    if (stickRef.current) {
      requestAnimationFrame(() => {
        el.scrollTop = el.scrollHeight;
      });
    }
  }, [messages, sessionId]);

  function onScroll(e: UIEvent<HTMLDivElement>) {
    const el = e.currentTarget;
    const atBottom =
      el.scrollHeight - el.scrollTop - el.clientHeight < 120;
    stickRef.current = atBottom;
  }

  if (!sessionId) {
    return (
      <div className="flex-1 flex items-center justify-center p-4">
        <div className="text-center space-y-2">
          <div className="text-sm text-muted-foreground">
            No session selected.
          </div>
          <div className="text-xs text-muted-foreground/70">
            Pick one on the left, or hit "New Session".
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex-1 min-h-0 relative">
      <div
        ref={scrollRef}
        onScroll={onScroll}
        className="absolute inset-0 overflow-y-auto overscroll-contain"
      >
        <div className="max-w-4xl mx-auto w-full px-3 sm:px-4 py-4 sm:py-6 space-y-4">
          {messages.length === 0 && (
            <div className="text-center text-sm text-muted-foreground/70 py-12">
              Send a prompt to begin.
            </div>
          )}
          {messages.map((m) => (
            <div
              key={m.id}
              data-message-id={m.id}
              ref={(el) => {
                const map = messageRefsRef.current;
                if (el) map.set(m.id, el);
                else map.delete(m.id);
              }}
            >
              <MessageCard msg={m} />
            </div>
          ))}
        </div>
      </div>
      <ThreadNav
        scrollRef={scrollRef}
        messageRefs={messageRefsRef}
        userMessageIds={userMessageIds}
      />
    </div>
  );
}

export default MessageThread;
